import { CrossIcon } from "@solomon-labs/design/components/icons";
import {
    Button,
    ButtonIcon,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerTitle,
    Text,
    textVariants
} from "@solomon-labs/design/components/ui";
import React from "react";

/* ── Types ──────────────────────────────────────────────── */

export interface MenuItem {
    /** Display label. */
    label: string;
    /** Unique key — used for selection tracking. */
    value: string;
    /** External link opens in new tab with arrow icon. */
    external?: boolean;
    /** Only show in mobile drawer. */
    mobileOnly?: boolean;
}

interface MenuProps {
    /** Menu items. */
    items: MenuItem[];
    /** Currently active item value. */
    activeValue?: string;
    /** Called when an item is selected. */
    onSelect: (value: string) => void;
}

/* ── Mobile: bottom drawer with vertical items ─────────── */

interface MobileMenuProps extends MenuProps {
    /** Whether the drawer is open. */
    isOpen: boolean;
    /** Close the drawer. */
    onClose: () => void;
    /** Optional title shown at top of drawer. */
    title?: string;
}

function MobileMenu({
    items,
    activeValue,
    onSelect,
    isOpen,
    onClose,
    title = "Menu"
}: MobileMenuProps): React.ReactElement {
    const handleSelect = (value: string): void => {
        onSelect(value);
        onClose();
    };

    return (
        <Drawer
            open={isOpen}
            onOpenChange={(v): void => {
                if (!v) onClose();
            }}
        >
            <DrawerContent className="max-h-[80vh] pt-0" aria-label={title}>
                <div className="flex flex-col py-4">
                    {/* Heading — hero variant with the brand ember accent
                        square, aligned to the top of the cap height. */}
                    <div className="flex items-start justify-between px-4 pb-5">
                        <div className="flex items-start gap-2">
                            <div className="mt-[5px] md:mt-[11px] size-1.5 bg-accent shrink-0" />
                            <DrawerTitle
                                className={textVariants({ variant: "hero" })}
                            >
                                {title}
                            </DrawerTitle>
                        </div>
                        <DrawerClose
                            render={
                                <ButtonIcon
                                    variant="transparent"
                                    size="xl"
                                    className="mt-1"
                                    aria-label="Close menu"
                                    icon={<CrossIcon />}
                                />
                            }
                        />
                    </div>

                    <div className="px-4">
                        <div className="h-px bg-line-base" />
                    </div>

                    {/* Nav items — h2 typography on the transparent nav-button
                        states (charcoal → ember on hover / aria-selected). */}
                    <nav className="flex flex-col gap-4 py-4">
                        {items.map((item) => {
                            const active = item.value === activeValue;
                            return (
                                <Button
                                    key={item.value}
                                    variant="transparent"
                                    size="sm"
                                    interaction="underline"
                                    onClick={() => handleSelect(item.value)}
                                    aria-selected={active ? "true" : undefined}
                                    fullWidth
                                    className="justify-start px-8 py-2.5 md:px-8"
                                    style={{ textUnderlineOffset: "4px" }}
                                >
                                    {item.external ? (
                                        <Text
                                            variant="h2"
                                            render={
                                                <span className="flex items-center gap-1.5" />
                                            }
                                        >
                                            {item.label}
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                            >
                                                <path
                                                    d="M3.5 2h6.5v6.5m0-6.5L2 10"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                />
                                            </svg>
                                        </Text>
                                    ) : (
                                        <Text variant="h2" render={<span />}>
                                            {item.label}
                                        </Text>
                                    )}
                                </Button>
                            );
                        })}
                    </nav>
                </div>
            </DrawerContent>
        </Drawer>
    );
}

/* ── Exports ────────────────────────────────────────────── */

export { MobileMenu };
export type { MenuProps, MobileMenuProps };
